import { useEffect, useState } from 'react'

interface ConfirmDialogProps {
  open: boolean
  title: string
  message?: string
  reasonLabel: string
  confirmLabel: string
  cancelLabel: string
  /** Called with the captured (non-empty) reason. */
  onConfirm: (reason: string) => void
  onCancel: () => void
}

/**
 * Confirm-with-reason modal (UX charter R6): destructive / state-changing
 * actions require an explicit, non-empty reason that is surfaced to onConfirm
 * (and ultimately the audit log). Confirm stays disabled until a reason is given.
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { open, title, message, reasonLabel, confirmLabel, cancelLabel, onConfirm, onCancel } = props
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!open) setReason('')
  }, [open])

  if (!open) return null
  const canConfirm = reason.trim().length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-900">
        <h2 className="text-lg font-semibold">{title}</h2>
        {message && <p className="mt-1 text-sm text-neutral-500">{message}</p>}
        <label className="mt-4 block text-sm">
          {reasonLabel}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 h-20 w-full rounded border border-neutral-300 p-2 text-sm dark:bg-neutral-800"
            autoFocus
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded px-3 py-2 text-sm text-neutral-600 dark:text-neutral-300">
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
            className="rounded bg-red-600 px-3 py-2 text-sm text-white disabled:opacity-40"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
